import { Component, OnInit, Input, ElementRef, ViewChild, Renderer2, HostListener, ViewChildren, QueryList } from '@angular/core';
import { IUProperty } from 'src/app/shared/interface/umbraco-property';
import { IPictureData } from '../../shared/components/picture/picture.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ILinkWithColor } from 'src/app/shared/interface/link';
import { ThumbnailBuilderService } from '../two-col-two-row-panel/service/thumbnail-builder.service';
import { AnimationOptions } from 'ngx-lottie';
import get from 'lodash.get';
import { IVideoPickerVideoData } from 'src/app/shared/interface/video-picker-data';
import { applySubtitleSettings, applyTitleSettings } from '../../shared/utils/title-style-settings-utils';
import { ISubtitleStyleSettings, ITitleStyleSettings } from '../../shared/interface/title-style-settings';
import { applyItemSettings } from '../../shared/utils/item-settings-utils';
import { IItemSettings } from '../../shared/interface/item-settings';
import { placementToAlign } from '../../shared/utils/placement-to-alignment';

interface ITwoColTwoRowPanelData {
  title: IUProperty<string>;
  subtitle: IUProperty<string>;
  capitalizeTitle?: IUProperty<string[]>;
  alignItems?: IUProperty<boolean>;
  order?: IUProperty<boolean>;
  backgroundImage?: IUProperty<IPictureData>;
  topWave: IUProperty<boolean>;
  bottomWave: IUProperty<boolean>;
  links?: IUProperty<ILinkWithColor[]>;
  items?: IUProperty<ITwoColTwoRowItem[]>;
  biggerSide?: IUProperty<string>;

  titleStyleSettings?: ITitleStyleSettings;
  subtitleStyleSettingsComposition?: ISubtitleStyleSettings;
  itemSettingsComposition?: IItemSettings;
}

interface ITwoColTwoRowItem {
  topSpacing?: IUProperty<string>;
  title?: IUProperty<string>;
  description?: IUProperty<string | SafeHtml>;
  videoData: IVideoViewModel;
  isShow: boolean;
  media?: IUProperty<{
    image?: IUProperty<IPictureData>;
    video?: IUProperty<{
      mobile: {
        loop: boolean;
        thumbnail: string;
        title: string;
        type: number;
        url: string;
        videoCode: string;
      },
      desktop: {
        loop: boolean;
        thumbnail: string;
        title: string;
        type: number;
        url: string;
        videoCode: string;
      },
    }>
  }>
  animationUrl?: IUProperty<string>;
  options: AnimationOptions;
  link: IUProperty<ILinkWithColor[]>;
}

export interface IVideoViewModel {
  video: object;
  thumbnail: IPictureData;
  params: string;
}

@Component({
  selector: 'app-two-col-two-row-panel',
  templateUrl: './two-col-two-row-panel.component.html',
  styleUrls: ['./two-col-two-row-panel.component.less']
})
export class TwoColTwoRowPanelComponent implements OnInit {
  @Input() data: ITwoColTwoRowPanelData;
  paddingTop: string;
  paddingBottom: string;
  descriptions: SafeHtml[] = [];
  scrollPosition: number = null;
  lazyLoad: boolean = false;
  isMobile: boolean;

  @ViewChild('panelElRef', { static: false }) panelElRef: ElementRef<HTMLElement>;
  @ViewChild('contentElRef', { static: false }) contentElRef: ElementRef<HTMLElement>;
  @ViewChild('titleEl', { static: false }) titleEl: ElementRef<HTMLElement>;
  @ViewChild('subtitleEl', { static: false }) subtitleEl: ElementRef<HTMLElement>;
  @ViewChild('wrapperEl', { static: false }) wrapperEl: ElementRef<HTMLElement>;
  @ViewChildren('videoHolder') videoHolderRefs: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren('itemEl') itemEls: QueryList<ElementRef<HTMLElement>>;

  @HostListener('window:resize', ['$event']) getScreenSize(event?) {
    if (this.videoHolderRefs && this.videoHolderRefs.length) {
      this.videoHolderRefs.forEach(ref => {
        if (ref && ref.nativeElement) {
          ref.nativeElement.style.minHeight = `${ref.nativeElement.getClientRects()[0].width * (9/16)}px`;
          //this is required for minHeight to work
          ref.nativeElement.style.height = '1px';
        }
      })
    }
  }

  @HostListener('window:scroll', []) onWindowScroll() {
    this.scrollPosition = window.pageYOffset;
    if ((this.scrollPosition + window.outerHeight) > this.panelElRef.nativeElement.offsetTop) {
      this.loadMedia();
      this.lazyLoad = true;
    }
  }

  constructor(
    private renderer: Renderer2,
    private thumbnailBuilder: ThumbnailBuilderService) { }

  ngOnInit() {
    this.scrollPosition = window.pageYOffset;
    this.prepareVideos();
  }

  ngAfterViewInit(): void {
    this.setItemCustomStyling();
    this.setBiggerSide();
    if (this.videoHolderRefs && this.videoHolderRefs.length) {
      this.videoHolderRefs.forEach(ref => {
        if (ref && ref.nativeElement) {
          ref.nativeElement.style.minHeight = `${ref.nativeElement.getClientRects()[0].width * (9/16)}px`;
          //this is required for minHeight to work
          ref.nativeElement.style.height = '1px';
        }
      })
    }

    if (this.data && this.data.capitalizeTitle && this.data.capitalizeTitle.value) {
      this.data.capitalizeTitle.value.forEach(item => {
        switch (item) {
          case 'Title': if (this.titleEl && this.titleEl.nativeElement) this.titleEl.nativeElement.style.textTransform = 'uppercase';
            break;
          case 'Subtitle': if (this.subtitleEl && this.subtitleEl.nativeElement) this.subtitleEl.nativeElement.style.textTransform = 'uppercase';
            break;
        }
      })
    }

    this.setPadding();
    applyTitleSettings(this.data, this.titleEl);
    applyItemSettings(this.data, this.wrapperEl);
    applySubtitleSettings(this.data, this.subtitleEl);

    if ((this.scrollPosition + window.outerHeight) > this.panelElRef.nativeElement.offsetTop) {
      this.loadMedia();
      this.lazyLoad = true;
    }
  }

  private setItemCustomStyling() {
    if (!this.itemEls) return;

    this.itemEls.forEach((ref, i) => {
      const itemData = this.data.items.value[i];

      if (this.data.itemSettingsComposition && this.data.itemSettingsComposition.itemTextSize && this.data.itemSettingsComposition.itemTextSize.value) {
        ref.nativeElement.style.setProperty('--rte-font-size', this.data.itemSettingsComposition.itemTextSize.value + 'px');
      }
      if (this.data.itemSettingsComposition && this.data.itemSettingsComposition.itemFontWeight && this.data.itemSettingsComposition.itemFontWeight.value) {
        ref.nativeElement.style.setProperty('--rte-font-weight', this.data.itemSettingsComposition.itemFontWeight.value + 'px');
      }
      if (itemData.topSpacing && itemData.topSpacing.value) {
        ref.nativeElement.style.setProperty('--twoColTwoRow-panel-top-spacing', itemData.topSpacing.value + 'px');
      }
    });
  }

  private setBiggerSide() {
    if (!this.data || !this.data.biggerSide || !this.data.biggerSide.value) return;
    if (this.data.items.value.length < 2) return;

    var side = this.data.biggerSide.value;
    var sizeStringMap = {
      "Left": "60% 40%",
      "Right": "40% 60%"
    };

    var sizeString = sizeStringMap[side] || "50% 50%";
    this.panelElRef.nativeElement.style.setProperty('--twocoltworow-panel-colSize', sizeString);
  }

  setPadding() {
    if (this.data.topWave.value) this.contentElRef.nativeElement.style.paddingTop = '115px';
    else this.contentElRef.nativeElement.style.paddingTop = '30px';

    if (this.data.bottomWave && this.data.bottomWave.value && this.data.links && this.data.links.value) {
      this.contentElRef.nativeElement.style.paddingBottom = '95px';
    } else if (this.data.bottomWave && this.data.bottomWave.value) {
      this.contentElRef.nativeElement.style.paddingBottom = '75px';
    } else if (this.data.links && this.data.links.value) {
      this.contentElRef.nativeElement.style.paddingBottom = '65px';
    } else {
      this.contentElRef.nativeElement.style.paddingBottom = '30px';
    }
  }

  prepareVideos() {
    this.data.items.value.forEach((item, i) => {
      if (item.media && item.media.value && item.media.value.video) {
        item.isShow = false;
        item.videoData = this.isMobile ? this.mobile(item) : this.desktop(item);
      }
      if (item.animationUrl && item.animationUrl.value) {
        item.options = {
          path: item.animationUrl.value
        }
      }
    });
  }

  changeToMobile() {
    this.isMobile = true;
  }

  changeToDesktop() {
    this.isMobile = false;
  }

  mobile(item) {
    return this.prepareVm(get(item, 'media.value.video.mobile'));
  }
  desktop(item) {
    return this.prepareVm(get(item, 'media.value.video.desktop'));
  }

  private prepareVm(data: IVideoPickerVideoData): IVideoViewModel {
    if (!data) return null;

    return {
      thumbnail: this.thumbnailBuilder.create(data.thumbnail),
      params: `?controls=1&muted=0&autoplay=1&showinfo=0`,
      video: {
        url: data.url,
        type: data.type,
      }
    };
  }

  open(item: any) {
    item.isShow = true;
  }

  close(item: any) {
    item.isShow = false;
  }

  loadMedia() {
    if (this.panelElRef && this.panelElRef.nativeElement && this.data && this.data.backgroundImage && this.data.backgroundImage.value) {
      this.panelElRef.nativeElement.style.backgroundImage = 'url(' + this.data.backgroundImage.value.src + ')';
      this.panelElRef.nativeElement.style.backgroundSize = 'cover';
      this.panelElRef.nativeElement.style.backgroundRepeat = 'no-repeat';
    }
  }

}

